import { Box, Card, Text } from '@mantine/core';

import { useT } from '../i18n';

/**
 * PRE-275: a one-series trend, small enough to sit in a stat card.
 *
 * Deliberately hand-rolled SVG rather than a chart dependency — three 24-point
 * series do not justify one, and the panel has no charting library today.
 *
 * One series per card, so there is no legend and no categorical palette: the card
 * title names the series and the hero number direct-labels its last value. The
 * only colour decision is `tone`, and `alert` is a status colour (reserved, and
 * always shipped with the text label above it), never "series 2".
 */

export type SparkPoint = { tsMs: number; value: number };

type Props = {
  label: string;
  points: SparkPoint[];
  /** Rendered as the hero number; receives the most recent value. */
  format?: (value: number) => string;
  sub?: string;
  tone?: 'neutral' | 'alert';
};

const WIDTH = 220;
const HEIGHT = 40;

/** Points → an SVG polyline path, scaled to the box. Baseline is always 0. */
export function sparkPath(points: SparkPoint[], width = WIDTH, height = HEIGHT): string {
  if (points.length === 0) return '';
  const max = Math.max(...points.map((p) => p.value), 0);
  // A flat series (all equal, including all-zero) draws along the bottom rather
  // than dividing by zero or floating in the middle of the box.
  const span = max === 0 ? 1 : max;
  const stepX = points.length === 1 ? 0 : width / (points.length - 1);
  return points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - (p.value / span) * height;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export function Sparkline({ label, points, format, sub, tone = 'neutral' }: Props) {
  const last = points.length > 0 ? points[points.length - 1]!.value : null;
  const peak = points.length > 0 ? Math.max(...points.map((p) => p.value)) : null;
  const stroke = tone === 'alert' ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-blue-6)';
  const t = useT();
  const summary = { label, last: last ?? 0, peak: peak ?? 0, count: points.length };

  return (
    <Card withBorder padding="md" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700} fz={24} mt={2}>
        {last == null ? '—' : format ? format(last) : String(last)}
      </Text>

      {points.length === 0 ? (
        // No line at all when there is no data. Drawing a flat line along zero
        // would claim the value WAS zero for 24 hours, which is a different and
        // much more reassuring statement than "we have no measurements".
        <Text size="xs" c="dimmed" mt={6}>
          {t('sparkline.noData')}
        </Text>
      ) : (
        <Box mt={6}>
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            width="100%"
            height={HEIGHT}
            preserveAspectRatio="none"
            role="img"
            aria-label={t('sparkline.aria', summary)}
          >
            <title>{t('sparkline.title', summary)}</title>
            <path
              d={sparkPath(points)}
              fill="none"
              stroke={stroke}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </Box>
      )}

      {sub && (
        <Text size="xs" c="dimmed" mt={4}>
          {sub}
        </Text>
      )}
    </Card>
  );
}
